import type { EventView } from "@confluence/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useState, type KeyboardEvent, type RefObject } from "react";
import { get } from "../api";
import { useI18n } from "../i18n";
import type { CalendarInfo } from "./data";
import { dtf } from "./format";
import { dayKeyIn, type DayKey } from "./time";

/** Recherche dans ses événements ; « / » place le curseur dans le champ. */
export function SearchBox({
  inputRef,
  tz,
  index,
  onOpen,
  onGo,
}: {
  inputRef: RefObject<HTMLInputElement | null>;
  tz: string;
  index: Map<string, CalendarInfo>;
  onOpen: (ev: EventView) => void;
  onGo: (date: DayKey) => void;
}) {
  const { t, locale } = useI18n();
  const id = useId();
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  useEffect(() => {
    const h = setTimeout(() => setQuery(text.trim()), 250);
    return () => clearTimeout(h);
  }, [text]);
  const results = useQuery({
    queryKey: ["search", query],
    queryFn: async () => (await get<{ events: EventView[] }>(`/api/events/search?q=${encodeURIComponent(query)}`)).events,
    enabled: query.length >= 2,
    staleTime: 30_000,
  });
  const list = open && query.length >= 2 ? (results.data ?? []) : [];
  const when = dtf(locale, { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: tz });

  const pick = (ev: EventView) => {
    onGo(ev.allDay ? ev.start.slice(0, 10) : dayKeyIn(ev.start, tz));
    onOpen(ev);
    setOpen(false);
    inputRef.current?.blur();
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    } else if (!list.length) return;
    else if (e.key === "ArrowDown") setActive((a) => (a + 1) % list.length);
    else if (e.key === "ArrowUp") setActive((a) => (a - 1 + list.length) % list.length);
    else if (e.key === "Enter") pick(list[active]!);
    else return;
    e.preventDefault();
  };

  return (
    <div className="search combo">
      <svg className="icon search-icon" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="11" cy="11" r="6.5" />
        <path d="M20 20l-4.2-4.2" />
      </svg>
      <input
        ref={inputRef}
        className="input input-sm"
        type="search"
        value={text}
        placeholder={t("search.placeholder")}
        aria-label={t("search.placeholder")}
        role="combobox"
        aria-expanded={list.length > 0}
        aria-controls={`${id}-r`}
        aria-activedescendant={list.length ? `${id}-${active}` : undefined}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKey}
      />
      {open && query.length >= 2 && (
        <ul className="poi search-results" id={`${id}-r`} role="listbox">
          {list.length === 0 && !results.isFetching && <li className="src">{t("search.none")}</li>}
          {list.map((ev, i) => {
            const cal = index.get(ev.calendarId);
            return (
              <li
                key={ev.id}
                id={`${id}-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(ev);
                }}
                style={{ ["--cal" as string]: cal?.color ?? "var(--accent)" }}
              >
                <span className="search-dot" aria-hidden="true" />
                <span className="pm">
                  <span className="pn">{ev.title || t("cal.noTitle")}</span>
                  <span className="pa">
                    {when.format(new Date(ev.allDay ? `${ev.start.slice(0, 10)}T12:00:00Z` : ev.start))}
                    {ev.location ? ` · ${ev.location}` : ""}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
